import { ARROW_BODY_CLASS } from '@lib/components/SmartArrow'
import type { ID } from '@octopus-do/octopus-project-core'

import { ARROW_LABEL_SEARCH_STEP } from './constants'

export interface Point {
  x: number
  y: number
}

// The drawn line of an arrow, piece by piece from its start to its end. A smooth
// arrow can have two pieces, an arrow with anchors has one per anchor.
export const getArrowLine = (
  arrowId: ID,
  root: ParentNode = document
): SVGPathElement[] =>
  Array.from(
    root.querySelectorAll<SVGPathElement>(
      `.Tree-arrows [data-arrow-id="${arrowId}"] .${ARROW_BODY_CLASS}`
    )
  )

const getScreenPoint = (
  piece: SVGPathElement,
  length: number
): Point | null => {
  const matrix = piece.getScreenCTM()

  if (!matrix) {
    return null
  }

  const { x, y } = piece.getPointAtLength(length)

  return {
    x: matrix.a * x + matrix.c * y + matrix.e,
    y: matrix.b * x + matrix.d * y + matrix.f,
  }
}

const getLengths = (line: SVGPathElement[]): number[] =>
  line.map((piece) => piece.getTotalLength())

const sum = (values: number[]): number =>
  values.reduce((total, value) => total + value, 0)

/** The point at a share (0-1) of the line's length, in screen coordinates. */
export const getPointAtShare = (
  line: SVGPathElement[],
  share: number
): Point | null => {
  const lengths = getLengths(line)
  const total = sum(lengths)

  if (total === 0) {
    return null
  }

  const target = total * share
  const index = lengths.findIndex(
    (_, i) => sum(lengths.slice(0, i + 1)) >= target
  )
  const pieceIndex = index === -1 ? line.length - 1 : index

  return getScreenPoint(
    line[pieceIndex],
    target - sum(lengths.slice(0, pieceIndex))
  )
}

/** The share (0-1) of the line's length closest to a point on the screen. */
export const getClosestShare = (
  line: SVGPathElement[],
  { x, y }: Point
): number | null => {
  const lengths = getLengths(line)
  const total = sum(lengths)

  if (total === 0) {
    return null
  }

  const steps = Math.ceil(total / ARROW_LABEL_SEARCH_STEP)
  const shares = Array.from({ length: steps + 1 }, (_, i) => i / steps)

  const distances = shares.map((share) => {
    const point = getPointAtShare(line, share)

    return point
      ? Math.hypot(point.x - x, point.y - y)
      : Number.POSITIVE_INFINITY
  })

  return shares[distances.indexOf(Math.min(...distances))]
}

/** Puts a label on its line; a label whose line isn't drawn stays out of sight. */
export const placeLabel = (
  label: HTMLElement,
  root: ParentNode = document
): void => {
  const layer = label.parentElement
  const { arrowId, position } = label.dataset

  const point =
    layer && arrowId
      ? getPointAtShare(getArrowLine(arrowId, root), Number(position))
      : null

  if (!layer || !point) {
    // Not `visibility`: the field has to take focus before its line is drawn.
    label.style.opacity = '0'
    label.style.pointerEvents = 'none'

    return
  }

  const origin = layer.getBoundingClientRect()

  label.style.left = `${point.x - origin.left}px`
  label.style.top = `${point.y - origin.top}px`
  label.style.removeProperty('opacity')
  label.style.removeProperty('pointer-events')
}

export const placeLabels = (layer: HTMLElement): void => {
  layer.querySelectorAll<HTMLElement>('[data-arrow-id]').forEach((label) => {
    placeLabel(label)
  })
}

/**
 * The PNG export lays its copy of the map out differently from the live one, so
 * the labels are placed again against the lines of that copy.
 */
export const placeExportedLabels = (root: HTMLElement): void => {
  root.querySelectorAll<HTMLElement>('[data-position]').forEach((label) => {
    placeLabel(label, root)
  })
}
